import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";

import { updateSection } from "../../assignmentSlice";

import {
  convertCircledNumberToDigit,
  convertDigitToCircledNumber,
  getTextByLabel,
} from "../../../../utils/textUtils";
import { getStartingIndex } from "../../../../utils/resumeUtils";

import BasicChoicePage from "./basic_choice/BasicChoicePage";
import ReadingBookPage from "./reading_book/ReadingBookPage";

const GrammarPage = ({ setCurrentStep, isPreview, isReview }) => {
  const dispatch = useDispatch();

  const { section } = useSelector((state) => state.assignment);

  const [pageList, setPageList] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);

  const currentPage = pageList[currentIndex] || {
    type: "",
  };

  const handlePreviousPage = () => {
    if (currentIndex === 0) return;
    setCurrentIndex((prev) => prev - 1);
  };

  const handleNextPage = () => {
    if (currentIndex < pageList.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    } else {
      const isAllAnswered = pageList.every((page) => page.isSubmitted);

      if (isAllAnswered) {
        // 딕테이션 메뉴(가상 섹션, id 0)는 복습이라 서버에 저장하지 않는다
        if (section.id === 0) {
          alert("받아쓰기 복습을 끝냈어요!");
          setCurrentStep(2);
          return;
        }
        if (!isPreview && !isReview) {
          dispatch(
            updateSection({
              sectionId: section.id,
              data: { is_finished: true, answer: pageList },
            })
          );
        }
        alert("학습이 완료되었습니다.");
        setCurrentStep(2);
      } else {
        alert("문제를 덜 풀었습니다.");
      }
    }
  };

  useEffect(() => {
    if (pageList.length > 0) return;

    if (!isPreview && section.answer.length > 0) {
      setPageList(section.answer);
      setCurrentIndex(getStartingIndex(section.resumeIndex));
      return;
    }

    setPageList(
      section.question.data.map((question, questionIndex) => {
        const newPage = {
          ...question,
          isCorrect: false,
          isSubmitted: false,
          answerData: question.answer,
        };

        // Grammar_low uses labeled choices (A, B, C...)
        if (["grammar_low"].includes(newPage.type)) {
          return {
            ...newPage,
            answer: convertCircledNumberToDigit(newPage.answer[0]),
            choices: newPage.choices.map(
              (choice) => `${choice} ${getTextByLabel(newPage.text, choice)}`
            ),
            text: newPage.text.replace(/--(.*?)--/g, "<u>$1</u>"),
          };
        }
        // Grammar_mid and grammar_high use numbered choices (①, ②, ③...)
        else if (["grammar_mid", "grammar_high"].includes(newPage.type)) {
          return {
            ...newPage,
            answer: convertCircledNumberToDigit(newPage.answer[0]),
            choices: newPage.choices.map(
              (choice, choiceIndex) =>
                `${convertDigitToCircledNumber(choiceIndex + 1)} ${choice}`
            ),
            text: newPage.text.replace(/--(.*?)--/g, "<u>$1</u>"),
          };
        }
        // Handle grammar:sentence type (choices embedded in question)
        // 한 문장에 **[a / b]** 괄호가 여러 개인 문항이 있다 (정답이
        // "① U.S., ② fighting" 처럼 합성, 문장당 최대 3개). 예전에는 첫 괄호만
        // 읽고 합성 정답을 못 찾아 정답 번호가 0 이 됐고, 무엇을 골라도 오답이
        // 됐다 (2026-08-25 실제 발생). 괄호가 하나면 기존 단일 선택 그대로,
        // 여러 개면 한 화면에서 괄호마다 하나씩 고르는 blanks 페이지로 만든다.
        else if (newPage.type === "grammar:sentence") {
          const groups = [...newPage.question.matchAll(/\*\*\[(.*?)\]\*\*/g)];
          if (groups.length) {
            // 꼬리 쉼표·마침표는 정답과 선지 표기가 어긋나는 흔한 지점이다
            const normalize = (value) =>
              String(value || "").trim().replace(/[,，.]+$/, "").trim();

            if (groups.length === 1) {
              const choices = groups[0][1].split(" / ").map((c) => c.trim());
              const answerIndex =
                choices.findIndex((c) => normalize(c) === normalize(newPage.answer)) + 1;
              return {
                ...newPage,
                answer: answerIndex,
                choices,
                text: "",
                question: newPage.question.replace(/\*\*\[(.*?)\]\*\*/, "____"),
              };
            }

            const answerParts = String(newPage.answer || "")
              .split(/[①②③④⑤⑥⑦⑧⑨]/)
              .map(normalize)
              .filter(Boolean);
            // question 은 **[a / b]** 마커를 그대로 둔다. 화면(BasicChoicePage)이
            // 원래 어법처럼 문장 안 볼드 괄호로 그리고, 푼 괄호는 정답으로 채운다.
            return {
              ...newPage,
              text: "",
              choices: [],
              answer: 0,
              blanks: groups.map((group, blankIndex) => {
                const choices = group[1].split(" / ").map((c) => c.trim());
                return {
                  choices,
                  answer:
                    choices.findIndex(
                      (c) => normalize(c) === (answerParts[blankIndex] || "")
                    ) + 1,
                };
              }),
            };
          }
        }

        return newPage;
      })
    );

    setCurrentIndex(0);
  }, [section]);

  // [문제뱅크 문해력] 객관식만 있는 문해력 세트는 전자책으로 푼다(지문 쪽 · 문항 쪽을 넘기며 종이 위에서 고르고
  // 빨간 펜으로 채점). 빈칸 · 주관식이 섞인 세트는 아래 어법 화면 그대로다.
  const isReadingBook =
    section.type === "reading" &&
    pageList.length > 0 &&
    pageList.every((page) => page.type === "grammar_mid" && (page.choices || []).length > 0) &&
    pageList.some((page) => page.passage);
  if (isReadingBook) {
    return (
      <ReadingBookPage
        section={section}
        pageList={pageList}
        setPageList={setPageList}
        setCurrentStep={setCurrentStep}
        isPreview={isPreview}
        isReview={isReview}
      />
    );
  }

  // All grammar types use BasicChoicePage
  // (bank_short/bank_cloze/bank_dictation 은 문제뱅크 주관식·받아쓰기. 목록에
  // 없으면 그 문항 차례에 아무것도 안 그려져 화면이 하얗게 된다.)
  if (
    [
      "grammar_low", "grammar_mid", "grammar_high", "grammar:sentence",
      "bank_short", "bank_cloze", "bank_dictation",
    ].includes(currentPage.type)
  ) {
    return (
      <BasicChoicePage
        setCurrentStep={setCurrentStep}
        isPreview={isPreview}
        isReview={isReview}
        section={section}
        currentPage={currentPage}
        currentIndex={currentIndex}
        pageList={pageList}
        setPageList={setPageList}
        handlePreviousPage={handlePreviousPage}
        handleNextPage={handleNextPage}
      />
    );
  }

  return <div></div>;
};

export default GrammarPage;
